import { useState } from 'react';
import { Chip, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { supportService } from '../../services/api/supportService';

interface AttachmentChipProps {
  blobPath: string;
  fileName?: string;
  getUrl?: (blobPath: string, fileName?: string) => Promise<string | undefined>;
  onDelete?: () => void;
}

async function defaultGetUrl(blobPath: string, fileName?: string) {
  const response = await supportService.getAttachmentUrl(blobPath, fileName);
  return response.data.data;
}

export function AttachmentChip({
  blobPath,
  fileName,
  getUrl = defaultGetUrl,
  onDelete,
}: AttachmentChipProps) {
  const [loading, setLoading] = useState(false);

  const label = fileName ?? blobPath.split('/').pop() ?? blobPath;

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const signedUrl = await getUrl(blobPath, fileName);
      if (signedUrl) window.open(signedUrl, '_blank');
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  return (
    <Chip
      icon={
        loading ? (
          <CircularProgress size={10} sx={{ ml: 0.5 }} />
        ) : (
          <AttachFileIcon sx={{ fontSize: '0.7rem !important' }} />
        )
      }
      label={label}
      size="small"
      onClick={handleClick}
      onDelete={onDelete}
      clickable={!loading}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        cursor: loading ? 'wait' : 'pointer',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

type AttachmentEntry = { path: string; name: string } | string;

/** Parses attachmentsJson and renders one chip per attachment. Returns null if empty/invalid. */
export function AttachmentList({
  json,
  getUrl,
}: {
  json?: string;
  getUrl?: (blobPath: string, fileName?: string) => Promise<string | undefined>;
}) {
  if (!json) return null;
  let entries: AttachmentEntry[] = [];
  try {
    entries = JSON.parse(json);
  } catch {
    return null;
  }
  if (!entries.length) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
      {entries.map((entry) => {
        const blobPath = typeof entry === 'string' ? entry : entry.path;
        const fileName = typeof entry === 'string' ? undefined : entry.name;
        return (
          <AttachmentChip
            key={blobPath}
            blobPath={blobPath}
            fileName={fileName}
            getUrl={getUrl}
          />
        );
      })}
    </div>
  );
}
